import { parsePath } from 'react-router-dom'
import { isSafeRelativePath } from '@/utils/urlSafety'

const KEY = 'graphit:auth-return-to'
const SETUP_KEY = 'graphit:setup-return-to'
const MAX_AGE_MS = 30 * 60 * 1000
// Where a finished setup lands when nothing asked to come back somewhere.
const DEFAULT_LANDING_PATHS = ['/', '/dashboards', '/getting-started']

function saveReturnPath(key, path) {
  try {
    if (isSafeRelativePath(path)) {
      localStorage.setItem(key, JSON.stringify({ path, expiresAt: Date.now() + MAX_AGE_MS }))
    } else clearReturnPath(key)
  } catch { /* Storage can be unavailable; ordinary sign-in still works. */ }
}

function readReturnPath(key) {
  try {
    const saved = JSON.parse(localStorage.getItem(key) || 'null')
    if (saved?.expiresAt > Date.now() && isSafeRelativePath(saved.path)) return saved.path
    clearReturnPath(key)
  } catch { clearReturnPath(key) }
  return null
}

function clearReturnPath(key) {
  try { localStorage.removeItem(key) } catch { /* Optional browser storage. */ }
}

// Email links may open in another tab; a short-lived, single-use local entry
// carries the destination through that hop and external provider redirects.
export function saveAuthReturnTo(path) { saveReturnPath(KEY, path) }
export function readAuthReturnTo() { return readReturnPath(KEY) }
export function clearAuthReturnTo() { clearReturnPath(KEY) }

// Organization setup (signup -> email verification -> plan -> Stripe) crosses
// tabs and a Stripe redirect, so the page that sent the user into it (e.g. the
// Rock link consent page) is kept in its own entry. Separate from the sign-in
// entry because LoginPage clears that one as soon as the user is authenticated,
// long before the new organization exists.
export function saveSetupReturnTo(path) {
  if (isDefaultLandingPath(path)) clearReturnPath(SETUP_KEY)
  else saveReturnPath(SETUP_KEY, path)
}
export function readSetupReturnTo() { return readReturnPath(SETUP_KEY) }
export function clearSetupReturnTo() { clearReturnPath(SETUP_KEY) }

/** True for the paths a finished sign-in or setup falls back to anyway. */
export function isDefaultLandingPath(path) {
  if (!isSafeRelativePath(path)) return true
  return DEFAULT_LANDING_PATHS.includes(parsePath(path).pathname || '/')
}

/**
 * Router state that carries `path` to the next page as `state.from` (the
 * shape ProtectedRoute uses), or undefined when there is nothing worth
 * carrying.
 */
export function returnState(path) {
  return path && !isDefaultLandingPath(path) ? { from: parsePath(path) } : undefined
}

export function authReturnOptions(path) {
  return path && path !== '/dashboards'
    ? { replace: true, state: { from: parsePath(path) } }
    : { replace: true }
}

// An invitation grants the first membership, so accepting it cannot require
// selecting an organization that the account does not belong to yet.
export function isInvitationReturnPath(path) {
  return isSafeRelativePath(path) && /^\/invite\/[^/]+\/?$/.test(parsePath(path).pathname || '')
}

/** The ChatGPT account-linking continuation (backend `/api/chatgpt/oauth/authorize/`). */
export const CHATGPT_CONNECT_PATH = '/integrations/chatgpt'
const SIGN_IN_LANDING_PATH = '/dashboards'

export function isChatGPTReturnPath(path) {
  return isSafeRelativePath(path) && parsePath(path).pathname === CHATGPT_CONNECT_PATH
}

/**
 * Where a finished sign-in goes. A ChatGPT account-linking continuation
 * only survives while ChatGPT is enabled for the user (`/me.chatgpt_enabled`);
 * otherwise the sign-in degrades to the normal landing page.
 */
export function signInDestination(path, { isChatGPTEnabled = false } = {}) {
  return isChatGPTReturnPath(path) && !isChatGPTEnabled ? SIGN_IN_LANDING_PATH : path
}

// Pages that pick (or create) the organization themselves, so a sign-in
// headed there skips /tenant-select: invitations, Rock link consent, and
// ChatGPT account linking (only while it is enabled). The consent pages
// select their own organization.
export function isTenantFreeReturnPath(path, { isChatGPTEnabled = false } = {}) {
  if (isInvitationReturnPath(path)) return true
  if (isChatGPTReturnPath(path)) return isChatGPTEnabled
  return isSafeRelativePath(path) && parsePath(path).pathname === '/integrations/link'
}

export function pathFromAuthState(location) {
  const from = location.state?.from
  const path = from?.pathname ? `${from.pathname}${from.search || ''}${from.hash || ''}` : null
  return isSafeRelativePath(path) ? path : null
}

/** The non-default destination this tab's router `state.from` carries, if any. */
export function carriedReturnPath(location) {
  const fromState = pathFromAuthState(location)
  return fromState && !isDefaultLandingPath(fromState) ? fromState : null
}

/**
 * Where to go once an organization setup finishes: the router's `state.from`
 * when this tab carried one, else the saved setup entry (a new tab opened
 * from the verification email, or the return from Stripe). Null means "the
 * normal first-run landing".
 */
export function setupReturnPath(location) {
  return carriedReturnPath(location) || readSetupReturnTo()
}
